import { getVestingTerms } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { getOrganizationVestingTerms } from '@dealstack/frontend/shared/dataExchange/services/programme-defaults-vesting.service';
import {
	VestingMode,
	VestingTermRes,
	VestingTermTimeUnitType,
	VestingTermType,
} from '@dealstack/shared/lib/generated';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { getLegalEntities } from '@dealstack/frontend/app/services/Person.service.ts';
import { useVestingTermMutations } from '@dealstack/frontend/shared/components/VestingTerms/hooks/useVestingTermMutations';
import { useVestingTermValidators } from '@dealstack/frontend/shared/components/VestingTerms/hooks/useVestingTermValidators';
import { useGradedMutations } from '@dealstack/frontend/shared/components/VestingTerms/hooks/useGradedMutations';
import { useStraightLineMutations } from '@dealstack/frontend/shared/components/VestingTerms/hooks/useStraightLineMutations';
import { useLeaverProvisionMutations } from '@dealstack/frontend/shared/components/VestingTerms/hooks/useLeaverProvisionMutations';
import { usePerformanceVestingMutations } from '@dealstack/frontend/shared/components/VestingTerms/hooks/usePerformanceVestingMutations';

const PLACEHOLDER_VESTING_TERM: VestingTermRes = {
	id: '',
	mipId: null,
	organizationId: null,
	name: 'New vesting term',
	type: VestingTermType.GRADED,
	vestingMode: VestingMode.VALUE,
	timeBasedPercentage: 100,
	performancePercentage: 0,
	unvestedPercentage: 0,
	optionPeriod: 12,
	optionPeriodTimeUnit: VestingTermTimeUnitType.MONTHLY,
	vestingTermGraded: [],
	vestingTermLeaverProvisions: [],
	hasLiveTransactions: false,
};

export type VestingTermsController = ReturnType<typeof useVestingTermsController>;

export const useVestingTermsController = (organizationId?: string) => {
	const { dealId } = useContext(DealContext);

	const [currentVestingTermId, setCurrentVestingTermId] = useState<string>('');

	const getVestingTermsRequest = useQuery({
		queryKey: organizationId
			? [API_PATHS.PROGRAMME_DEFAULTS_VESTING_TERM_ORGANIZATIONID_GET, { organizationId }]
			: [API_PATHS.MIP_VESTING_TERM_GET],
		queryFn: ({ signal }) =>
			organizationId
				? getOrganizationVestingTerms(organizationId, signal)
				: getVestingTerms(API_PATHS.MIP_VESTING_TERM_GET, signal),
	});
	const { data: legalEntities } = useQuery({
		queryKey: [API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, { dealId }],
		queryFn: () => getLegalEntities(API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, { dealId }),
		enabled: !!dealId,
	});

	const vestingTerms = getVestingTermsRequest.data?.payload;

	const personOptions = useMemo(
		() =>
			legalEntities?.map((entity) => ({
				value: entity.id,
				label: entity.displayName,
			})) ?? [],
		[legalEntities]
	);

	const currentVestingTerm = useMemo(
		() =>
			vestingTerms?.find((term) => term.id === currentVestingTermId) ??
			PLACEHOLDER_VESTING_TERM,
		[vestingTerms, currentVestingTermId]
	);

	useEffect(() => {
		if (vestingTerms?.[0] && !currentVestingTermId) {
			setCurrentVestingTermId(vestingTerms[0].id);
		}
	}, [vestingTerms, currentVestingTermId]);

	const refetchVestingTerms = getVestingTermsRequest.refetch;

	const termMutations = useVestingTermMutations({
		vestingTerms,
		currentVestingTermId,
		setCurrentVestingTermId,
		refetchVestingTerms,
		organizationId,
	});
	const validators = useVestingTermValidators(
		currentVestingTerm,
		termMutations.onUpdateVestingTerm
	);
	const gradedMutations = useGradedMutations(refetchVestingTerms, organizationId);
	const straightLineMutations = useStraightLineMutations(refetchVestingTerms, organizationId);
	const leaverProvisionMutations = useLeaverProvisionMutations(
		refetchVestingTerms,
		organizationId
	);
	const performanceVestingMutations = usePerformanceVestingMutations(
		currentVestingTerm,
		refetchVestingTerms
	);

	return useMemo(
		() => ({
			vestingTerms,
			currentVestingTerm,
			currentVestingTermId,
			setCurrentVestingTermId,
			personOptions,
			isPerformanceVestingAvailable: !organizationId,
			...termMutations,
			...validators,
			...gradedMutations,
			...straightLineMutations,
			...leaverProvisionMutations,
			...performanceVestingMutations,
		}),
		[
			vestingTerms,
			currentVestingTerm,
			currentVestingTermId,
			personOptions,
			organizationId,
			termMutations,
			validators,
			gradedMutations,
			straightLineMutations,
			leaverProvisionMutations,
			performanceVestingMutations,
		]
	);
};
